import { Button, Icon, Price, TextPassage } from '@petsmart-ui/sparky';
import { Fragment } from 'react';

import { ResponsivePicture } from '@libs/shared-ui';

import { getAddToCartButtonLabel, type MappedFBTProduct } from '../lib';
import { FBTProductLink } from './FBTProductLink';

export interface FBTProductImagesTotalProps {
    products: MappedFBTProduct[];
    selectedProductIds: Record<string, boolean>;
    onAddToCart: () => Promise<void>;
    isAddingToCart?: boolean;
}

const IMG_SIZE_FBT_PRODUCT = {
    width: 120,
    height: 120,
};

export function FBTProductImagesTotal({
    products,
    selectedProductIds,
    onAddToCart,
    isAddingToCart,
}: Readonly<FBTProductImagesTotalProps>) {
    const selectedProducts = products.filter(product => selectedProductIds[product.id]);

    const hasSelectedProducts = selectedProducts.length > 0;
    const hasProductsInStock = selectedProducts.find(product => product.inStock);
    const isAddToCartDisabled = isAddingToCart || !hasSelectedProducts || !hasProductsInStock;

    const cartSubtotal = selectedProducts.reduce((result, product) => result + product.displayPrice, 0);
    const cartQuantity = selectedProducts.length;
    const cartQuantityMessage = cartQuantity > 1 ? 'items' : 'item';

    if (!hasSelectedProducts) {
        return (
            <TextPassage className="fbt__images-total">
                <p className="fbt__images-message">Choose items to buy together</p>
            </TextPassage>
        );
    }

    if (!hasProductsInStock) {
        return (
            <TextPassage className="fbt__images-total">
                <p className="fbt__images-message">These frequently bought items are out of stock in your area</p>
            </TextPassage>
        );
    }

    return (
        <div className="fbt__images-total" data-testid="fbt-images-total">
            <ul className="fbt__images">
                {selectedProducts.map((product, index) => {
                    const ResponsivePictureWrapper = product.url ? FBTProductLink : Fragment;
                    const hasMore = index + 1 < selectedProducts.length;

                    return (
                        <li
                            key={product.id}
                            className="fbt__images-item"
                            data-testid="test-product-recommendations-visibility"
                        >
                            <ResponsivePictureWrapper href={product.url} title={product.name}>
                                <ResponsivePicture
                                    className="frequently-bought-together__image"
                                    src={product.displayImage}
                                    alt={`${product.name} image`}
                                    size={IMG_SIZE_FBT_PRODUCT}
                                />
                            </ResponsivePictureWrapper>
                            {hasMore && <Icon className="c-image-list__icon fbt__plus-sign" name="plus" />}
                        </li>
                    );
                })}
            </ul>
            <div className="fbt__add-to-cart">
                <TextPassage className="fbt__subtotal">
                    <span className="sparky-u-margin-right-xs">Subtotal:</span>
                    <Price
                        className="sparky-u-display-inline sparky-u-margin-right-xs"
                        variant="sale"
                        values={[cartSubtotal]}
                    />
                    ({cartQuantity} {cartQuantityMessage})
                </TextPassage>
                <Button
                    className="fbt__add-to-cart-button"
                    data-testid="test-fbt-add-to-cart-button"
                    onClick={onAddToCart}
                    loading={isAddingToCart}
                    disabled={isAddToCartDisabled}
                    text={getAddToCartButtonLabel({
                        quantity: cartQuantity,
                        productsCount: products.length,
                    })}
                />
            </div>
        </div>
    );
}
